import { useCheckoutContext } from 'context/CheckoutContext';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/macro';
import { Alert, AlertDescription } from 'components/ui/alert';

// Height: full viewport minus title bar and control bar (~112px)
const CHART_HEIGHT = 'calc(100vh - 112px)';

export default function SeatsPicker() {
    const {
        event,
        errorMessage,
        setSeatsPicked,
        products,
        handleAddProduct,
        handleRemoveProduct,
        handleSessionInitialized,
        handleSessionExpired,
        setChartRef,
        setChartSelectionErrors,
    } = useCheckoutContext();

    const chartRef = useRef(null);

    const { i18n } = useLingui();

    const formatCurrency = (value) =>
        i18n.number(value, { style: 'currency', currency: event.currency || 'CAD' });

    // Store chart reference in context when it changes
    const handleChartRendered = useCallback(
        (chart) => {
            chartRef.current = chart;
            setChartRef(chart);
        },
        [setChartRef],
    );

    // Clear chart ref when unmounting
    useEffect(() => {
        return () => {
            setChartRef(null);
        };
    }, [setChartRef]);

    const hasMultiLevel = useMemo(() => {
        return products.some(
            (p) =>
                p.type === 3 &&
                p.seatPricing &&
                Array.isArray(p.seatPricing) &&
                p.seatPricing.length > 0,
        );
    }, [products]);

    // Helper to convert category key to the correct type for seats.io
    // SeatsIO lets you set the category key to either a number or string
    // whole numbers >= 1 (and no leading zeroes) will be numbers
    // anything else is a string for seatsio purposes
    // we store category keys as varchar in our DB so they are string by default
    const convertCategoryKey = (categoryId) => {
        const categoryKeyRegex = /^[1-9][0-9]*$/;
        if (categoryKeyRegex.test(categoryId)) {
            return Number(categoryId);
        }
        return categoryId;
    };

    const pricing = useMemo(() => {
        const seatProducts = products.filter((p) => p.type === 3);

        if (hasMultiLevel) {
            // Multi-level pricing mode
            // Group by category, then add ticket types for each category
            const categoryMap = new Map();

            seatProducts.forEach((product) => {
                product.seatPricing?.forEach((sp) => {
                    const categoryId = convertCategoryKey(sp.seat_category_id);

                    if (!categoryMap.has(categoryId)) {
                        categoryMap.set(categoryId, {
                            category: categoryId,
                            ticketTypes: [],
                        });
                    }

                    categoryMap.get(categoryId).ticketTypes.push({
                        ticketType: String(product.id),
                        label: product.name,
                        price: sp.price,
                    });
                });
            });

            // Convert to pricing array, using simple format for categories with only one ticket type
            // Simple format auto-selects without showing a popup
            const pricing = Array.from(categoryMap.values()).map((cat) => {
                if (cat.ticketTypes.length === 1) {
                    // Single ticket type - use simple pricing (no popup)
                    return {
                        category: cat.category,
                        ticketType: cat.ticketTypes[0].ticketType,
                        price: cat.ticketTypes[0].price,
                    };
                }
                // Multiple ticket types - use ticketTypes array (shows popup)
                return cat;
            });

            return {
                priceFormatter: (price) => formatCurrency(price),
                prices: pricing,
            };
        }
        // Single pricing (backwards compatibility)
        const pricingConfig = {
            priceFormatter: (price) => formatCurrency(price),
            prices: seatProducts.map((p) => ({
                category: p.name,
                price: p.price,
            })),
        };

        return pricingConfig;
        // eslint-disable-next-line
    }, [products, hasMultiLevel]);

    // Calculate available categories based on which categories have tickets
    // Categories without any ticket types should be unavailable for selection
    const availableCategories = useMemo(() => {
        if (!hasMultiLevel) {
            // For single pricing mode, all categories with products are available
            // The category is the product name in this mode
            return undefined; // Don't restrict categories in single pricing mode
        }

        // For multi-level pricing, extract category IDs from pricing config
        // These are the only categories that have tickets available
        const categoryIds = pricing.prices.map((p) => p.category);

        // If no categories have tickets, return empty array to make all unavailable
        return categoryIds.length > 0 ? categoryIds : [];
    }, [hasMultiLevel, pricing]);

    // Helper to find product for a seat selection
    // For multi-level pricing (multiple ticket types), ticket.ticketType contains the product ID
    // For simple pricing (single ticket type per category), we look up by category key
    const findProductForSeat = useCallback(
        (obj, ticket) => {
            // Multi-level: ticket contains the product ID
            if (ticket?.ticketType) {
                return products.find((p) => String(p.id) === ticket.ticketType);
            }

            // Simple pricing: find the product that has this category in its seatPricing
            const categoryKey = String(obj.category?.key);
            return products.find(
                (p) =>
                    p.type === 3 &&
                    p.seatPricing?.some((sp) => String(sp.seat_category_id) === categoryKey),
            );
        },
        [products],
    );

    const handleObjectSelected = useCallback(
        (obj, ticket) => {
            // Use functional setState to avoid depending on seatsPicked
            setSeatsPicked((prevSeats) => {
                // Check if Seat or Table is already selected
                if (
                    (obj.objectType === 'Seat' || obj.objectType === 'Table') &&
                    prevSeats.find((s) => s.uuid === obj.uuid)
                ) {
                    return prevSeats;
                }
                // Check if General Admission area is already selected
                // When seating chart is restored, the callback is run for every previously selected GA seat.
                // We only add a new seat if the selected count is higher than the current count to avoid duplicates being created.
                // Note: We may want to stop adding multiple recs per seat, and just keep one with a count of how many were selected.
                if (obj.objectType === 'GeneralAdmissionArea') {
                    let numSelected = 0;
                    let prevNumSelected = 0;

                    if (ticket?.ticketType) {
                        // Multi-level
                        // Figure out how many selections for this GA Area + Product combo are in the cart
                        numSelected = obj.selectionPerTicketType[ticket.ticketType];
                        prevNumSelected = prevSeats.reduce((acc, seat) => {
                            if (
                                Number(seat.selectedTicketType) === Number(ticket.ticketType) &&
                                seat.uuid === obj.uuid
                            ) {
                                return acc + 1;
                            } else {
                                return acc;
                            }
                        }, 0);
                    } else {
                        // Simple pricing
                        numSelected = obj.numSelected;
                        prevNumSelected = prevSeats.reduce(
                            (acc, seat) => (seat.uuid === obj.uuid ? acc + 1 : acc),
                            0,
                        );
                    }

                    // This GA seat has already been selected
                    if (numSelected <= prevNumSelected) {
                        return prevSeats;
                    }
                }

                // Find the product based on the selected ticket type (using product id)
                const product = findProductForSeat(obj, ticket);

                // For single pricing (legacy), update cart
                if (!hasMultiLevel && product) {
                    handleAddProduct(product.id);
                }

                // Add productId to the seat object for backend processing
                const seatWithProduct = {
                    ...obj,
                    productId: product?.id || null,
                };

                return [...prevSeats, seatWithProduct];
            });
        },
        [hasMultiLevel, findProductForSeat, handleAddProduct, setSeatsPicked],
    );

    const handleObjectDeselected = useCallback(
        (obj, ticket) => {
            // Get the ticket type ID for GA area matching
            const ticketTypeId = ticket?.ticketType;

            if (obj.objectType === 'Seat' || obj.objectType === 'Table') {
                setSeatsPicked((s) => s.filter((el) => el.uuid !== obj.uuid));
            } else if (obj.objectType === 'GeneralAdmissionArea') {
                setSeatsPicked((s) => {
                    let removed = false; // General Admission Areas can have multiple tickets and ticket types selected at once. Remove the first matching ticket and stop there so we don't clear out all the GA tickets.
                    return s.filter((el) => {
                        if (
                            removed === false &&
                            el.uuid === obj.uuid &&
                            el.selectedTicketType === ticketTypeId
                        ) {
                            removed = true;
                            return false;
                        } else {
                            return true;
                        }
                    });
                });
            }

            // For multi-level pricing, we don't use the cart system
            if (!hasMultiLevel) {
                // Single pricing: use cart system
                const product = findProductForSeat(obj, ticket);
                if (product) {
                    handleRemoveProduct(product.id);
                }
            }
        },
        [hasMultiLevel, findProductForSeat, handleRemoveProduct, setSeatsPicked],
    );

    // *** Selection Callbacks *** //
    // One of these will fire every time the user makes a selection.
    // User selection is checked against the selectionValidators

    const handleSelectionValid = useCallback(() => {
        setChartSelectionErrors([]);
    }, [setChartSelectionErrors]);

    const handleSelectionInvalid = useCallback(
        (violations = []) => {
            setChartSelectionErrors(violations);
        },
        [setChartSelectionErrors],
    );

    return (
        <div
            className="flex w-full flex-col select-none bg-background"
            style={{ height: CHART_HEIGHT }}
        >
            {errorMessage && (
                <Alert variant="destructive" className="shrink-0 rounded-none">
                    <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
            )}
            {event.parentMode && event.seatsSeasonEnabled && (
                <p className="m-0 shrink-0 border-b border-border bg-card px-4 py-3 text-center text-sm text-foreground">
                    <Trans>Your season ticket reserves the same seat at every event.</Trans>
                </p>
            )}
            <div className="min-h-0 flex-1">
                <SeatsioSeatingChart
                    workspaceKey={event.seatsPublicKey}
                    event={event.seatsEventKey}
                    region="na"
                    showFullScreenButton={false}
                    categoryFilter={{
                        enabled: true,
                        multiSelect: false,
                        zoomOnSelect: false,
                        sortBy: 'price',
                    }}
                    {...DEFAULT_SEAT_CHART_CONFIG}
                    {...event.seatChartConfig}
                    language={i18n.locale}
                    onSelectionValid={handleSelectionValid}
                    onSelectionInvalid={handleSelectionInvalid}
                    onObjectSelected={handleObjectSelected}
                    onObjectDeselected={handleObjectDeselected}
                    onChartRendered={handleChartRendered}
                    pricing={pricing}
                    availableCategories={availableCategories}
                    session="continue"
                    onSessionInitialized={handleSessionInitialized}
                    onHoldTokenExpired={handleSessionExpired}
                />
            </div>
        </div>
    );
}

const DEFAULT_SEAT_CHART_CONFIG = {
    colorScheme: 'dark',
    selectionValidators: [{ type: 'noOrphanSeats' }],
};
